import { Decimal } from '@capsicum/common';
import {
	type OnWalletErrorShape,
	type UseBuyTransactionReturn,
	useGetPurchaseErrorMessage
} from '@capsicum/features/payments/shared';
import { type MinMaxLimit } from '@capsicum/socios-core';
import { atom, useAtom } from 'jotai';
import { atomWithReset } from 'jotai/utils';
import { useEffect, useMemo } from 'react';
import { type FieldError } from 'react-hook-form';

export const DEFAULT_MIN_PURCHASE_AMOUNT = 1;

export const purchaseFTOAmountAtom = atomWithReset(new Decimal(DEFAULT_MIN_PURCHASE_AMOUNT));
export const purchaseFTOErrorAtom = atom<FieldError | undefined>(undefined);

type UsePurchaseFTOAmountAtomProps = {
	chzDeficit?: Decimal;
	onWalletError?: OnWalletErrorShape;
	transactionError?: UseBuyTransactionReturn['transactionError'];
};

export const usePurchaseFTOAmountAtom = ({
	chzDeficit,
	onWalletError,
	transactionError
}: UsePurchaseFTOAmountAtomProps = {}) => {
	const getPurchaseErrorMessage = useGetPurchaseErrorMessage();
	const [purchaseAmount, setPurchaseAmount] = useAtom(purchaseFTOAmountAtom);
	const [purchaseError, setPurchaseError] = useAtom(purchaseFTOErrorAtom);

	/**
	 * Used to avoid call of cost estimate endpoint if the entered amount is out of range
	 * Otherwise cost estimate will return an error.
	 */
	const setPurchaseAmountWithValidation = (amount: Decimal, redirectToTopUp: () => void, limits?: MinMaxLimit) => {
		const minValue = new Decimal(limits?.minValue.limit || 0);
		const maxValue = !limits?.maxValue ? null : limits.maxValue.limit;

		if (amount.lessThan(minValue) || (maxValue && (maxValue.lessThan(minValue) || amount.greaterThan(maxValue)))) {
			const errorMessage = getPurchaseErrorMessage(
				redirectToTopUp,
				amount.lessThan(minValue) ? limits?.minValue : limits?.maxValue,
				onWalletError
			);

			setPurchaseError({
				message: errorMessage,
				type: ''
			});
		} else {
			setPurchaseAmount(amount);
			setPurchaseError(undefined);
		}
	};

	const amountError = useMemo(() => {
		const insufficientChzError = {
			type: '',
			withoutMessage: true
		};

		if (purchaseError) return purchaseError;
		if (chzDeficit && chzDeficit.greaterThan(0)) return insufficientChzError;
		if (transactionError) return transactionError;

		return undefined;
	}, [chzDeficit, purchaseError, transactionError]);

	useEffect(() => {
		setPurchaseError(amountError);
	}, [amountError, setPurchaseError]);

	return {
		amountError,
		purchaseAmount,
		purchaseError,
		setPurchaseAmount,
		setPurchaseAmountWithValidation
	};
};
